/**
 * Google Sign-In Client using Google Identity Services (GIS) with Popup Flow
 * This is an alternative implementation that uses the renderButton approach
 * instead of the One Tap prompt, providing the same interface as GoogleSignInClient.js
 *
 * @param {string} clientId - Google OAuth 2.0 client ID
 * @returns {Object} Client interface with login, revoke, and destroy methods
 *
 * docs: https://developers.google.com/identity/gsi/web/guides/overview
 * ref: https://developers.google.com/identity/gsi/web/reference/js-reference
 */
const GoogleSignInPopupClient = (clientId) => {
  const clientUrl = "https://accounts.google.com/gsi/client";
  let client = null;
  let initialized = false;
  let scriptLoadPromise = null;

  let responseHandler = null;
  let errorHandler = null;

  // Store the button container for cleanup
  let buttonContainer = null;

  // Helper function to parse JWT token
  const parseJwt = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new Error("Invalid JWT token format: " + error.message, { cause: error });
    }
  };

  const loadClientScript = async () => {
    // Return cached promise to prevent multiple script loads
    if (scriptLoadPromise) {
      return scriptLoadPromise;
    }

    scriptLoadPromise = new Promise((resolve, reject) => {
      if (client) {
        resolve(client);
        return;
      }

      let script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.onload = (event) => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
          client = window.google.accounts.id;
          resolve(client);
        } else {
          scriptLoadPromise = null; // Reset on failure
          reject({ error: "google_accounts_not_available", event: event });
        }
      };
      script.onerror = (event) => {
        scriptLoadPromise = null; // Reset on failure
        reject({ error: "google_accounts_failed_to_load", event: event });
      };
      document.querySelector("body").appendChild(script);
      script.src = clientUrl;
    });

    return scriptLoadPromise;
  };

  const initializeClient = async () => {
    const client = await loadClientScript();

    if (!initialized) {
      client.initialize({
        client_id: clientId,
        callback: (response) => {
          if (responseHandler) {
            responseHandler(response);
          }
        },
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: false, // Disable FedCM for popup flow
      });
      initialized = true;
    }

    return client;
  };

  const createButtonContainer = () => {
    // Create a temporary container for the Google button
    // This will be rendered when login() is called
    const container = document.createElement("div");
    container.id = "google-signin-popup-btn-" + Date.now();
    container.style.position = "absolute";
    container.style.left = "-9999px"; // Hide off-screen initially
    container.style.opacity = "0";
    container.style.pointerEvents = "none";
    document.body.appendChild(container);
    return container;
  };

  const cleanupButtonContainer = () => {
    if (buttonContainer && buttonContainer.parentNode) {
      buttonContainer.parentNode.removeChild(buttonContainer);
      buttonContainer = null;
    }
  };

  return {
    /**
     * Preload the Google Sign-In client script and initialize it
     * This ensures the client is ready before user interaction to avoid popup blocking
     * @returns {Promise} Resolves when client is ready, rejects on error
     */
    preload: async () => {
      return initializeClient();
    },

    /**
     * Initiate Google Sign-In flow using Popup Button
     * This creates an invisible button and programmatically clicks it to trigger the popup
     *
     * @param {Function} callback - Success callback receiving user data object
     * @param {Function} errorCallback - Error callback receiving error object
     */
    login: async (callback, errorCallback) => {
      const handleError = (error) => {
        // Clear cancel detection timeout if it exists
        if (buttonContainer?.dataset?.cancelTimeout) {
          clearTimeout(parseInt(buttonContainer.dataset.cancelTimeout));
        }
        if (errorCallback) errorCallback(error);
        cleanupButtonContainer();
        responseHandler = null;
        errorHandler = null;
      };

      const handleSuccess = (payload) => {
        // Clear cancel detection timeout if it exists
        if (buttonContainer?.dataset?.cancelTimeout) {
          clearTimeout(parseInt(buttonContainer.dataset.cancelTimeout));
        }
        if (callback) callback(payload);
        cleanupButtonContainer();
        responseHandler = null;
        errorHandler = null;
      };

      // Set up response handler for this login attempt
      responseHandler = (response) => {
        if (!response.credential) {
          handleError({
            error: "missing_credential",
            message: "No credential returned from Google Sign-In",
          });
          return;
        }

        let payload;
        try {
          payload = parseJwt(response.credential);
        } catch (error) {
          handleError({
            error: "failed_to_parse_credential",
            message: "Failed to parse JWT token",
            details: error.message,
          });
          return;
        }

        // Validate required fields exist
        if (!payload.sub) {
          handleError({
            error: "invalid_token_payload",
            message: "Token payload missing required 'sub' field",
          });
          return;
        }

        handleSuccess({
          service: "google",
          id: payload.sub,
          name: payload.name || "",
          picture: payload.picture || "",
          email: payload.email || "",
          email_verified: payload.email_verified || false,
          // id token should be verified on the backend
          // https://developers.google.com/identity/gsi/web/guides/verify-google-id-token
          id_token: response.credential,
        });
      };

      // Store error handler
      errorHandler = errorCallback;

      try {
        const client = await initializeClient();

        // Clean up any existing button container
        cleanupButtonContainer();

        // Create a new button container
        buttonContainer = createButtonContainer();

        // Render the Google Sign-In button
        client.renderButton(buttonContainer, {
          theme: "outline",
          size: "large",
          text: "signin_with",
          width: 300,
          logo_alignment: "left",
        });

        // Wait a moment for the button to render
        setTimeout(() => {
          // Find and click the button to trigger the popup
          const googleButton = buttonContainer.querySelector('[role="button"]');
          if (googleButton) {
            googleButton.click();

            // Set a timeout to detect if user cancelled the popup
            // If neither success nor error callback fires within 30 seconds,
            // assume the popup was cancelled
            const cancelDetectionTimeout = setTimeout(() => {
              if (responseHandler || errorHandler) {
                // Callbacks are still registered, meaning no response was received
                handleError({
                  error: "popup_cancelled",
                  message: "Google Sign-In timout, no response received in 30 seconds, resetting state",
                });
              }
            }, 30000);

            // Store the timeout so we can clear it in handleSuccess/handleError
            buttonContainer.dataset.cancelTimeout = cancelDetectionTimeout;
          } else {
            handleError({
              error: "button_not_rendered",
              message: "Google Sign-In button failed to render",
            });
          }
        }, 100);
      } catch (error) {
        // Handle both script loading errors and initialization errors
        const sanitizedError = {
          error: error.error || "login_failed",
          message: error.message || "Failed to initialize Google Sign-In",
        };
        // Only include details for known error types
        if (error.error === "google_accounts_not_available" || error.error === "google_accounts_failed_to_load") {
          sanitizedError.details = error.details || error.message;
        }
        handleError(sanitizedError);
      }
    },

    /**
     * Revoke the user's granted consent
     * @param {string} userEmail - User's email address (required for revocation)
     * @param {Function} callback - Success callback
     * @param {Function} errorCallback - Error callback receiving error object
     * @see https://developers.google.com/identity/gsi/web/guides/revoke
     */
    revoke: async (userEmail, callback, errorCallback) => {
      if (!userEmail) {
        if (errorCallback)
          errorCallback({
            error: "missing_email",
            message: "User email is required for revocation",
          });
        return;
      }
      try {
        const client = await loadClientScript();

        client.revoke(userEmail, (response) => {
          if (!response.successful) {
            if (errorCallback)
              errorCallback({
                error: "revoke_failed",
                message: "Failed to revoke user consent",
                response: response,
              });
            return;
          }
          if (callback) callback(response);
        });
      } catch (error) {
        if (errorCallback) errorCallback(error);
      }
    },

    /**
     * Clean up resources and reset client state
     * Call this when the client is no longer needed
     */
    destroy: () => {
      cleanupButtonContainer();
      responseHandler = null;
      errorHandler = null;
      initialized = false;
      scriptLoadPromise = null;
      // Note: We don't remove the script tag or reset the client object
      // as other instances might be using the same global Google API
    },
  };
};

export default GoogleSignInPopupClient;
